<template>
  <div>
    <vx-card class="mb-5" title="Learning and Resources">
      <h4 class="mb-5">A day in the life</h4>
      <p class="mb-5">
        Acting isn’t just about turning up and reading a script - there are lots of other things
        that actors need to consider too - like vocal warm ups, breathing exercises, articulation
        and auditioning.
      </p>

      <h4 class="mb-5">How to do Vocal Warm Ups</h4>
      <div class="vx-row mt-10 mb-5">
        <div class="vx-col xl:w-2/3 w-full mx-auto">
          <responsive-iframe-youtube
            :src="intro_videos[1].video"
            :posterimg="intro_videos[1].image"
          />
        </div>
      </div>

      <h4 class="mb-5">How to do Breathing exercises</h4>
      <div class="vx-row mt-10 mb-5">
        <div class="vx-col xl:w-2/3 w-full mx-auto">
          <responsive-iframe-youtube
            :src="intro_videos[2].video"
            :posterimg="intro_videos[2].image"
          />
        </div>
      </div>

      <h4 class="mb-5">Articulation</h4>
      <div class="vx-row mt-10 mb-5">
        <div class="vx-col xl:w-2/3 w-full mx-auto">
          <responsive-iframe-youtube
            :src="intro_videos[3].video"
            :posterimg="intro_videos[3].image"
          />
        </div>
      </div>

      <h4 class="mb-5">Auditioning</h4>
      <p class="mb-5">
        Auditioning is a really important part of an actor's life, and it’s what you will spend most
        of your time focusing on. Whether it’s for a role on stage, TV or film, you will be asked to
        either do an audition in front of the casting director or film yourself and send it to them.
        This is to make sure you are the right fit for the role.
      </p>
      <p class="mb-5">
        The more you go to auditions, the more likely it is that you will get roles - and if you
        keep doing good work, directors, casting directors and producers will start to take notice.
        These days it is common to send audition videos simply via your iphone!
      </p>

      <h4 class="mb-5">Auditioning tips:</h4>
      <ol class="numbered-list">
        <li>Keep it simple - don’t overcomplicate</li>
        <li>Be playful - audiences want to see you having fun!</li>
        <li>Use your natural voice</li>
        <li>
          Get comfortable - practice scenes with camera as much as possible and be familiar with
          acting on camera
        </li>
        <li>
          Don’t overly characterise - find how the character relates to you. What about you is
          similar to the character?
        </li>
        <li>
          Be able to quickly integrate feedback - typically for an audition they will give you notes
          or ask you to approach your performance in a different way to see how you take direction
        </li>
      </ol>

      <h4 class="mb-5">Self Taping</h4>
      <p class="mb-5">
        Self taping is becoming more and more popular in the industry. It is where you do a screen
        test (audition to camera) in your own time and ideally with your own equipment, at home or
        at a studio. It’s become its own style of audition and should be looked at from a different
        angle.
      </p>
      <p class="mb-5">Self taping tips:</p>
      <ol class="numbered-list">
        <li>Use a simple background – light blue is a great neutral colour</li>
        <li>Keep your eyeline close to the camera</li>
        <li>Have a good reader (someone to read lines with - preferably not Mum!)</li>
        <li>Try not to do lots of takes (3 takes per scene is enough)</li>
        <li>Don’t look for perfection</li>
      </ol>

      <h4 class="mb-5">Stage acting vs screen acting</h4>
      <p class="mb-5">
        Stage acting and acting for TV or film are very different for lots of reasons. Let’s take a
        look at ways in which they differ.
      </p>

      <h4 class="mb-5">Facial Expressions and Body Gestures</h4>
      <p class="mb-5">
        The biggest difference between acting for stage compared to acting for screen is the
        audience. In a theatre, the stage is much further away from the audience. This means,
        depending on the size of the theatre, the actors really have to exaggerate their facial
        expressions and gestures so even people right in the back row can see what’s going on.
      </p>
      <p class="mb-5">
        For example, actors aren’t able to express fear with just a look onstage, as only the
        audience members closest to the stage will see it! They might have to use their entire body
        to convey fear by cowering or flinching.
      </p>
      <p class="mb-5">
        When acting on screen, however, the camera can get extremely close to the actor, which
        closes the gap between the audience and the actors. Because of the close-up perspective,
        actors on film must use more subtle, controlled, and natural expressions and gestures.
        Large, exaggerated stage acting can look awkward and silly on screen!
      </p>

      <h4 class="mb-5">Voice and Volume</h4>
      <p class="mb-5">
        In stage acting, it’s important to have a strong and healthy voice, and to ensure that lines
        are memorised. This is because for each live theatre performance, actors have just one
        chance to get it right!
      </p>
      <p class="mb-5">
        Depending on the show or production budget, microphones may or may not be used during a
        performance. Even with a microphone, actors must practice and perform their lines accurately
        each time, with crisp and clear pronunciation and articulation. In musicals, actors must get
        their notes and lyrics correct on the first try.
      </p>
      <p class="mb-5">
        Yet for screen performances, actors have a number of ‘takes’ to get a scene right. If they
        stumble over a line or mess up the words, they can do the scene again and again. Film and
        television sets have microphones everywhere on the set to pick up the lines. In
        post-production, actors frequently go back to re-record lines to fix any errors that they
        made during filming.
      </p>

      <h4 class="mb-5">The Stanislavski Technique</h4>

      <p class="mb-5">
        Born in 1863, Konstantin Stanislavski was a Russian actor, director and theatre
        practitioner. Stanislavski Technique stems from his theatre practice and is still used by
        actors all around the world today.
      </p>
      <p class="mb-5">
        Stanislavski developed the technique in the early 1900s and they have been used ever since.
        His technique helps actors create believable emotions and actions and helps actors to build
        truly believable characters.
      </p>
      <p class="mb-5">Stanislavski method acting is split into seven steps, these are:</p>
      <ol class="numbered-list">
        <li>Who Am I?</li>
        <li>Where Am I?</li>
        <li>When Is It?</li>
        <li>What Do I Want?</li>
        <li>Why Do I Want It?</li>
        <li>How Will I Get It?</li>
        <li>What Do I Need To Overcome?</li>
      </ol>

      <h4 class="mb-5">The Magic ‘if’</h4>
      <p class="mb-5">
        An actor's job is to be believable, so to help achieve this Stanislavski created the 'Magic
        If', which involves an actor asking themselves "what would I do if I found myself in this
        (the character's) situation?”
      </p>
      <p class="mb-5">
        The ‘magic if’ simply involves an actor putting themselves in the character’s shoes within a
        certain scenario and asking the question ‘how would I react if this happened to me?’ By
        asking this simple question, an actor can understand the thoughts and feelings that they
        need to portray for each scene.
      </p>

      <h4 class="mb-5">Nonverbal acting</h4>

      <p class="mb-5">
        Nonverbal acting means, for the most part, acting without using any speech. You might think
        it sounds a bit strange that you can act without talking. But if you think back to a TV
        series you might have watched recently, you will find that a lot of actors have to master
        ‘nonverbal’ performances as part of their acting toolkit.
      </p>
      <p class="mb-5">
        That includes body movement, gestures and facial expressions. Nonverbal communication makes
        up anywhere from 65–95 percent of our communication, so it goes without saying that it is a
        really important part of human behaviour!
      </p>
      <p class="mb-5">
        An audition requiring only a reaction from you brings it to 100 percent. Since we are in the
        communication business, it makes sense that all actors master this nonverbal language.
      </p>
      <p class="mb-5">
        One way of using facial expression to give a powerful performance is to use eye contact,
        tears in your eyes, frowning or even subtle changes in your expression.
      </p>
      <p class="mb-5">
        Take a look at this behind-the-scenes clip of a performance by actor Naomi Watts - notice
        her facial expressions and how they convey a lot of emotion.
      </p>
      <p class="mb-5"><a href="" target="_blank">https://www.youtube.com/watch?v=fZJupdrv7ZQ</a></p>
    </vx-card>
  </div>
</template>

<script>
import store from '@/store/store';
import ResponsiveIframeYoutube from '../../../components/UI/ResponsiveIframeYoutube.vue';

export default {
  components: { ResponsiveIframeYoutube },
  data() {
    return {
      videoURL: false,
      showVideoPopupFile: false,
      showVideoPopupYt: false,
      ytVideoUrl: false,
      intro_videos: {
        1: {
          image: require('@/assets/images/experiences/actor/vocal_warm_up.jpg'),
          video: 'https://www.youtube.com/embed/CFXqyl4C1J4?start=151',
        },
        2: {
          image: require('@/assets/images/experiences/actor/breathing_exercises.jpg'),
          video: 'https://www.youtube.com/embed/dnxZYQe9fx0',
        },
        3: {
          image: require('@/assets/images/experiences/actor/articulation.jpg'),
          video: 'https://www.youtube.com/embed/CFXqyl4C1J4?start=489',
        },
      },
    };
  },
  beforeMount() {
    store.commit('authStudent/setActiveExperienceID', this.$route.params.id);
  },
};
</script>
